import { useApolloClient } from "@apollo/client";
import { Modal } from "@msys/ui";
import { Grid } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { uniqueId } from "lodash-es";
import moment from "moment";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import { AbsenceReason } from "../../../clients/graphqlTypes.js";
import { DatePickerField } from "../../commons/form-fields/DatePickerField.js";
import { SelectField } from "../../commons/form-fields/SelectField.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import { Stack } from "../../commons/layout/Stack.js";
import { PlanningAbsenceFragment } from "../../main-routes/planning/PlanningAbsences.generated.js";
import { OrganisationMemberField } from "../organisations/form-fields/OrganisationMemberField.js";
import { useModifyAbsenceMutation } from "./EditAbsenceModal.generated.js";
import { useAbsenceReasons } from "./useAbsenceReasons.js";

const FILTERS = { statuses: ["ACTIVATED" as const] };

interface FormValues {
  whoId: string | null;
  from: moment.Moment | null;
  till: moment.Moment | null;
  reason: AbsenceReason;
  note: string;
}

interface Props {
  absence: PlanningAbsenceFragment;
  refetchQueries?: string[];
  handleClose: () => void;
  handleComplete?: () => void;
}

export const EditAbsenceModal = ({
  absence,
  refetchQueries,
  handleClose,
  handleComplete,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["Global", "Absences"]);

  const client = useApolloClient();
  const [modifyAbsence] = useModifyAbsenceMutation({ client });

  const handleSubmit = async (values: FormValues) => {
    try {
      await modifyAbsence({
        variables: {
          input: {
            id: absence.id,
            whoId: values.whoId!,
            from: values.from!.format("YYYY-MM-DD"),
            till: values.till!.format("YYYY-MM-DD"),
            reason: values.reason,
            note: values.note,
          },
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      enqueueSnackbar(
        t("Absence updated", {
          ns: "Absences",
        })
      );
      handleComplete?.();
      handleClose();
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  const initialValues: FormValues = {
    whoId: absence.who.id,
    from: moment(absence.from),
    till: moment(absence.till),
    reason: absence.reason,
    note: absence.note,
  };

  const { absenceReasonOptions } = useAbsenceReasons();

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        whoId: Yup.string()
          .label(
            t("Team member", {
              ns: "Absences",
            })
          )
          .required(),
        from: Yup.date()
          .label(
            t("From", {
              ns: "Absences",
            })
          )
          .required(),
        till: Yup.date()
          .label(
            t("Until", {
              ns: "Absences",
            })
          )
          .test(
            "laterThan",
            t("Until must be later than from", {
              ns: "Absences",
            }),
            function (till: Date | null | undefined) {
              const from: Date | null | undefined = this.resolve(
                Yup.ref("from")
              );
              return !till || !from || from <= till;
            }
          )
          .required(),
        note: Yup.string().label(
          t("Note", {
            ns: "Absences",
          })
        ),
        reason: Yup.string()
          .label(
            t("Reason", {
              ns: "Absences",
            })
          )
          .required(),
      }),
    [t]
  );

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<FormValues>
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={handleSubmit}
      initialValues={initialValues}
    >
      {({ isSubmitting, dirty, isValid }) => (
        <Modal
          title={t("Edit absence", {
            ns: "Absences",
          })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Close", {
                ns: "Global",
              }),
              handleClick: handleClose,
              buttonProps: { variant: "text" },
            },
            {
              label: t("Save", {
                ns: "Global",
              }),
              buttonProps: {
                loading: isSubmitting,
                form: formId,
                type: "submit",
                disabled: !dirty || !isValid,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack flexDirection="column">
              <OrganisationMemberField
                name="whoId"
                required
                inputLabel={t("Team member", {
                  ns: "Absences",
                })}
                filters={FILTERS}
              />
              <SelectField
                name="reason"
                required
                options={absenceReasonOptions}
                label={t("Reason", {
                  ns: "Absences",
                })}
              />
              <Grid container spacing={1}>
                <Grid size={6}>
                  <DatePickerField
                    required
                    name="from"
                    label={t("From", {
                      ns: "Absences",
                    })}
                  />
                </Grid>
                <Grid size={6}>
                  <DatePickerField
                    required
                    name="till"
                    label={t("Until", {
                      ns: "Absences",
                    })}
                  />
                </Grid>
              </Grid>
              <TextField
                label={t("Note", {
                  ns: "Absences",
                })}
                name="note"
                multiline
                rows={3}
              />
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
